import React, { useState } from "react";
import {
  ArrowLeft,
  User,
  CreditCard,
  Clock,
  Printer,
  Download,
  RotateCcw,
  ExternalLink,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";
import {
  useRemoteUtilityId,
  useCurrency,
} from "@shared/selectors/globalSelectors";
import { usePaymentDetail } from "@features/cx/payments/hooks";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import { PaymentDetailsTab } from "./tabs/paymentDetails";
import { RefundPaymentDialog } from "../refundPayment/RefundPaymentDialog";
import { toast } from "@shared/hooks/use-toast";
import TimelineTab from "../../shared/TimelineTab";
import { PaymentReceipt } from "../../Detail/PaymentReceipt";

const PaymentDetailsV2 = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const [isRefundOpen, setIsRefundOpen] = useState(false);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [receiptAction, setReceiptAction] = useState<
    "download" | "print" | null
  >(null);

  const { data } = usePaymentDetail({
    paymentId: Number(id),
    remoteUtilityId: Number(remoteUtilityId),
  });

  const payment = data?.result;
  console.log(payment);

  const getStatusColor = (status: string) => {
    const s = status?.toLowerCase() || "";
    if (s === "posted")
      return {
        border: "border-l-green-500",
        badgeBg: "bg-green-50",
        badgeText: "text-green-700",
        badgeBorder: "border-green-200",
        dot: "bg-green-500",
      };
    if (s === "pending")
      return {
        border: "border-l-amber-500",
        badgeBg: "bg-amber-50",
        badgeText: "text-amber-700",
        badgeBorder: "border-amber-200",
        dot: "bg-amber-500",
      };
    if (s === "refunded")
      return {
        border: "border-l-red-500",
        badgeBg: "bg-red-50",
        badgeText: "text-red-700",
        badgeBorder: "border-red-200",
        dot: "bg-red-500",
      };
    if (s === "processed")
      return {
        border: "border-l-blue-500",
        badgeBg: "bg-blue-50",
        badgeText: "text-blue-700",
        badgeBorder: "border-blue-200",
        dot: "bg-blue-500",
      };
    return {
      border: "border-l-gray-500",
      badgeBg: "bg-gray-50",
      badgeText: "text-gray-700",
      badgeBorder: "border-gray-200",
      dot: "bg-gray-500",
    };
  };

  const statusColors = getStatusColor(payment?.statusDisplay);
  const isMultipleBills =
    Array.isArray(payment?.billData) && payment.billData.length > 1;
  const billCount = Array.isArray(payment?.billData)
    ? payment.billData.length
    : 0;

  const originalAmount = parseFloat(payment?.amount || "0");
  const refundedAmount = payment?.creditNoteDetails
    ? parseFloat(payment.creditNoteDetails.amount || "0")
    : 0;
  const netAmount = originalAmount - refundedAmount;

  const showIssueRefund =
    payment?.paymentPayType === "Bill Payment" &&
    originalAmount > 0 &&
    ["Posted", "Pending"].includes(payment?.paymentReceivedStatus || "");

  const tabComponents = {
    details: {
      label: "Details",
      component: <PaymentDetailsTab payment={payment} />,
    },
    audit: {
      label: "Audit Trail",
      component: (
        <TimelineTab
          remoteUtilityId={Number(remoteUtilityId)}
          entityId={payment?.receiptNo}
          actionType="bulk_request"
        />
      ),
    },
  };

  const urlMapping = {
    details: "details",
    audit: "audit",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700 hover:text-gray-900 transition-colors"
          onClick={() => navigate("/cx/payments")}
        />

        {/* Page Header */}
        <div className="flex flex-col">
          <PageHeader
            title="Payment Details"
            subtitle="View breakdown and receipt information for payment"
          />
        </div>
      </div>

      <div
        className={`bg-white border-l-4 ${statusColors.border} border border-gray-200 rounded-lg shadow-sm`}
      >
        <div className="p-6 pb-5">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-[22px] font-bold text-gray-800 tracking-tight font-mono">
                  {payment?.receiptNo || "N/A"}
                </h2>
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-600 border-none font-medium px-2.5 py-0.5 rounded-full text-xs"
                >
                  {payment?.paymentPayType || "Payment"}
                </Badge>
                {isMultipleBills && (
                  <Badge
                    variant="outline"
                    className="bg-gray-100 text-gray-600 border-none font-medium px-2.5 py-0.5 rounded-full text-xs"
                  >
                    {billCount} bills
                  </Badge>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <User className="w-[15px] h-[15px] text-gray-400" />
                    <span className="font-medium text-gray-700">
                      {payment?.consumer?.firstName}{" "}
                      {payment?.consumer?.lastName}
                    </span>
                    <span>·</span>
                    <span>{payment?.consumer?.consumerNo || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CreditCard className="w-[15px] h-[15px] text-gray-400" />
                    <span>{payment?.paymentMode || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-[15px] h-[15px] text-gray-400" />
                    <span>{payment?.paymentDate}</span>
                  </div>
                </div>
                {payment?.extraData?.remark && (
                  <div className="text-purple-600 text-[13px] font-medium italic">
                    {payment.extraData.remark}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-3 mt-1 sm:mt-0">
              <Badge
                variant="outline"
                className={`${statusColors.badgeBg} ${statusColors.badgeText} ${statusColors.badgeBorder} px-3 py-1 flex items-center gap-1.5 rounded-full font-medium`}
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full ${statusColors.dot}`}
                ></div>
                {payment?.paymentReceivedStatus || "N/A"}
              </Badge>

              <div className="text-right">
                <div className="text-[10px] font-bold text-gray-400 tracking-widest uppercase mb-0.5">
                  Amount
                </div>
                <div className="text-[26px] font-bold text-slate-800 leading-none font-mono">
                  {currency} {originalAmount?.toFixed(2)}
                </div>
                {refundedAmount > 0 && (
                  <div className="text-red-500 text-xs font-medium mt-1 font-mono">
                    - {currency} {refundedAmount?.toFixed(2)} refunded · Net{" "}
                    {currency} {netAmount?.toFixed(2)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 p-4 px-6 flex items-center flex-wrap gap-3">
          <Button
            variant="outline"
            className="flex items-center gap-2 h-9 text-gray-700 hover:text-gray-900 hover:bg-gray-50 border-gray-200 rounded-md shadow-sm"
            onClick={() => {
              setReceiptAction("print");
              setIsReceiptOpen(true);
            }}
          >
            <Printer className="w-[15px] h-[15px]" />
            <span className="text-[13px] font-medium">Print Receipt</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setReceiptAction("download");
              setIsReceiptOpen(true);
            }}
          >
            <Download className="w-[15px] h-[15px]" />
            <span className="text-[13px] font-medium">Download PDF</span>
          </Button>
          {showIssueRefund && (
            <Button
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
              onClick={() => setIsRefundOpen(true)}
            >
              <RotateCcw className="w-[15px] h-[15px]" />
              <span className="text-[13px] font-medium">Issue Refund</span>
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6">
        <Tabs
          defaultValue="details"
          className="w-full"
          tabsListClassName="grid grid-cols-2"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          level={0}
        />
      </div>

      {isRefundOpen && payment && (
        <RefundPaymentDialog
          isOpen={isRefundOpen}
          onClose={() => setIsRefundOpen(false)}
          payment={{
            ...payment,
            consumerFirstName:
              payment.consumerFirstName || payment.consumer?.firstName,
            consumerLastName:
              payment.consumerLastName || payment.consumer?.lastName,
            consumerNo: payment.consumerNo || payment.consumer?.consumerNo,
            consumerId: payment.consumerId || payment.consumer?.id,
            paymentModeDisplay:
              payment.paymentModeDisplay || payment.paymentMode,
          }}
        />
      )}

      {isReceiptOpen && payment?.id && (
        <PaymentReceipt
          isOpen={isReceiptOpen}
          onClose={() => {
            setIsReceiptOpen(false);
            setReceiptAction(null);
          }}
          paymentId={payment.id}
          formData={payment}
          autoDownload={receiptAction === "download"}
          autoPrint={receiptAction === "print"}
        />
      )}
    </div>
  );
};

export default PaymentDetailsV2;
